import { useTheme } from '../app/ThemeProvider.js';

/**
 * Area colours are stored once per area (areas.color) and were picked for the
 * dark surface — a muted, harmonic palette that reads well against deep navy.
 * On the light theme several of them wash out, so we darken them just enough to
 * stay legible while keeping each area recognisably the same hue.
 *
 * The stored value is never changed; this only affects how it is drawn.
 */

interface Hsl {
  h: number;
  s: number;
  l: number;
}

function hexToHsl(hex: string): Hsl | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;

  const int = parseInt(match[1]!, 16);
  const r = ((int >> 16) & 255) / 255;
  const g = ((int >> 8) & 255) / 255;
  const b = (int & 255) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return { h: h * 360, s, l };
}

function hslToCss({ h, s, l }: Hsl): string {
  return `hsl(${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%)`;
}

/**
 * Returns the colour to draw an area in for the given theme.
 * Dark theme: unchanged. Light theme: capped lightness and a touch more
 * saturation, so a pale cyan does not disappear against a white panel.
 */
export function areaColorFor(color: string | null | undefined, theme: 'dark' | 'light'): string {
  const fallback = theme === 'light' ? 'hsl(201 96% 32%)' : '#38BDF8';
  if (!color) return fallback;
  if (theme === 'dark') return color;

  const hsl = hexToHsl(color);
  if (!hsl) return color;

  return hslToCss({
    h: hsl.h,
    s: Math.min(1, hsl.s + 0.1),
    // 42 % lightness keeps every hue in the palette above 4.5:1 on white.
    l: Math.min(hsl.l, 0.42),
  });
}

/** React hook form — adjusts an area colour for the active theme. */
export function useAreaColor(): (color: string | null | undefined) => string {
  const { resolvedTheme } = useTheme();
  return (color) => areaColorFor(color, resolvedTheme);
}
